import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, SxProps } from '@mui/material'
import { CalendarIcon } from '@/assets'
import { useTranslations } from '@/core/translation'
import { Box } from '@/components/Layout'
import { Popper } from '@/components/Surface'
import DateRangePicker from '../DateRangePicker'
import Input from '../Input'
import { dateRangePickerInputStyles as styles } from './DateRangePickerInput.styles'
import { DateRangePickerInputProps } from './DateRangePickerInput.types'
import { getDateLabel } from './DateRangePickerInput.utils'

function DateRangePickerInput({
  value,
  onChange,
  singleDate,
  onFocus,
  showBlankOnNull = false,
  wrapperProps = {},
  datePickerProps = {},
  popperProps = {},
  ...rest
}: DateRangePickerInputProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const t = useTranslations('datePicker')

  const onChangeWrapper = useCallback(
    (newValue: NonNullable<DateRangePickerInputProps['value']>) => {
      if (singleDate || (newValue.startDate && newValue.endDate)) setIsCalendarOpen(false)
      onChange(newValue)
    },
    [onChange, singleDate],
  )

  const inputValue = useMemo(
    () => getDateLabel({ value, showBlankOnNull, singleDate }),
    [value, showBlankOnNull, singleDate],
  )

  // props extractions
  const { sx: datePickerSx = {}, ...otherDatePickerProps } = datePickerProps
  const { sx: popperSx = {}, ...otherPopperProps } = popperProps
  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsCalendarOpen(false)
      }}>
      <Box ref={wrapperRef} {...wrapperProps}>
        <Input
          inputRef={inputRef}
          placeholder={t('placeholder')}
          startAdornment={<CalendarIcon />}
          label={t('label')}
          onFocus={(event) => {
            setIsCalendarOpen(true)
            onFocus?.(event)
            inputRef.current?.blur()
          }}
          value={inputValue}
          active={isCalendarOpen}
          {...rest}
        />

        <Popper
          open={isCalendarOpen}
          anchorEl={wrapperRef.current}
          sx={{ ...styles.popperWrapper, ...popperSx } as SxProps}
          {...otherPopperProps}>
          <DateRangePicker
            value={value}
            onChange={onChangeWrapper}
            singleDate={singleDate}
            sx={{ ...styles.datePickerWrapper, ...datePickerSx } as SxProps}
            {...otherDatePickerProps}
          />
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default memo(DateRangePickerInput)
